import { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { sessionState } from '@chainlit/react-client';

import {
    awaitingInputThreadIdsState,
    generatingThreadIdsState,
    unseenGeneratedThreadIdsState
} from './threadGenerationState';

const withThreadId = (ids: Set<string>, threadId: string, present: boolean) => {
    if (ids.has(threadId) === present) return ids;
    const next = new Set(ids);
    if (present) next.add(threadId);
    else next.delete(threadId);
    return next;
};

/**
 * Same-tab only. Listens for thread_generation_start/thread_generation_end
 * (emitted from claude_managed/handle_runnable_messages.py around
 * live_generation_registry.register/unregister), each carrying the real
 * thread_id — NOT the generic task_start/task_end, which also fire on every
 * socket reconnect (e.g. useChatInteract.clear()'s disconnect() when
 * switching threads) regardless of whether that thread's generation
 * actually finished, and don't carry a thread_id at all.
 */
export function useThreadGenerationListener(currentThreadId: string | undefined) {
    const session = useRecoilValue(sessionState);
    const setGenerating = useSetRecoilState(generatingThreadIdsState);
    const setUnseen = useSetRecoilState(unseenGeneratedThreadIdsState);
    const setAwaitingInput = useSetRecoilState(awaitingInputThreadIdsState);

    useEffect(() => {
        if (!session?.socket) return;

        const handleStart = (event: { thread_id: string }) => {
            const threadId = event?.thread_id;
            if (!threadId) return;
            setGenerating((prev) => {
                if (prev.has(threadId)) return prev;
                const next = new Set(prev);
                next.add(threadId);
                return next;
            });
        };

        const handleEnd = (event: { thread_id: string }) => {
            const threadId = event?.thread_id;
            if (!threadId) return;

            setGenerating((prev) => {
                if (!prev.has(threadId)) return prev;
                const next = new Set(prev);
                next.delete(threadId);
                return next;
            });

            setUnseen((prev) => withThreadId(prev, threadId, true));
            setAwaitingInput((prev) => withThreadId(prev, threadId, false));
        };

        const handleAwaitingInput = (event: { thread_id: string }) => {
            const threadId = event?.thread_id;
            if (!threadId) return;
            setAwaitingInput((prev) => withThreadId(prev, threadId, true));
        };

        const handleInputReceived = (event: { thread_id: string }) => {
            const threadId = event?.thread_id;
            if (!threadId) return;
            setAwaitingInput((prev) => withThreadId(prev, threadId, false));
        };

        session.socket.on('thread_generation_start', handleStart);
        session.socket.on('thread_generation_end', handleEnd);
        session.socket.on('thread_awaiting_input', handleAwaitingInput);
        session.socket.on('thread_input_received', handleInputReceived);

        return () => {
            session.socket.off('thread_generation_start', handleStart);
            session.socket.off('thread_generation_end', handleEnd);
            session.socket.off('thread_awaiting_input', handleAwaitingInput);
            session.socket.off('thread_input_received', handleInputReceived);
        };
    }, [session?.socket, setGenerating, setUnseen, setAwaitingInput]);

    // Clear the dot the moment the user actually opens that thread.
    useEffect(() => {
        if (!currentThreadId) return;
        setUnseen((prev) => {
            if (!prev.has(currentThreadId)) return prev;
            const next = new Set(prev);
            next.delete(currentThreadId);
            return next;
        });
    }, [currentThreadId, setUnseen]);
}
